import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import moment from 'moment';
import Header from '../Include/Header';
import Sidebar from '../Include/Sidebar';
import Footer from '../Include/Footer';
import { useDispatch, useSelector } from 'react-redux';
import { getStoreDashboard } from '../../../Store/Store/Dashboard/Dashboard';
import toast from 'react-hot-toast';
import axios from 'axios';
import { M_STORE, V_URL } from '../../../BaseUrl';
import { Pagination } from '../Table';
import ItemWisePurchaseChart from './components/ItemWisePurchaseChart';
import { ShoppingBag, FileText, Send, Package, Box, Users } from 'lucide-react';
import ProjectUsageSummary from './ProjectUsageSummary';

const Dashboard = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (localStorage.getItem('PAY_USER_TOKEN') === null) {
            navigate("/user/login");
        } else if (localStorage.getItem('VI_PRO') !== `${M_STORE}`) {
            toast.error('Access Denied. You do not have permission to view this product. Please contact your administrator for assistance.');
            navigate("/user/login");
        }
    }, [navigate]);

    useEffect(() => {
        const fetchDashboard = () => {
            try {
                dispatch(getStoreDashboard());
            } catch (error) {
                console.log(error, '!!');
            }
        };
        fetchDashboard();
    }, [dispatch]);

    const storeState = useSelector((state) => state?.getStoreDashboard);
    const dashboardData = storeState?.user?.data;
    const isLoading = storeState?.loading;

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const handleOpen = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Filter states for Transaction Table
    const [transTagFilter, setTransTagFilter] = useState('ALL');
    const [transSearch, setTransSearch] = useState('');
    const [transPage, setTransPage] = useState(1);
    const transPerPage = 10;

    // Filter states for Party Summary Table
    const [partySearch, setPartySearch] = useState('');
    const [partyPage, setPartyPage] = useState(1);
    const partyPerPage = 10;

    // Filter states for Re-Order Quantity Management Table
    const [reorderSearch, setReorderSearch] = useState('');
    const [reorderStockFilter, setReorderStockFilter] = useState('ALL'); // 'ALL' | 'OUT_OF_STOCK' | 'LOW_STOCK'
    const [reorderPage, setReorderPage] = useState(1);
    const reorderPerPage = 10;
    const [isExportingReorder, setIsExportingReorder] = useState(false);

    const handleExportReorderExcel = async () => {
        try {
            setIsExportingReorder(true);
            const payload = {
                search: reorderSearch || '',
                firm_id: localStorage.getItem('PAY_USER_FIRM_ID'),
                year_id: localStorage.getItem('PAY_USER_YEAR_ID'),
            };

            const res = await axios.post(
                `${V_URL}/prosafe/pro-reorder-item-xlsx-download`,
                payload,
                {
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('PAY_USER_TOKEN'),
                    },
                }
            );

            if (res?.data && res?.data?.success) {
                const url = res?.data?.data?.url;
                if (url) {
                    window.open(url, '_blank');
                    toast.success(res?.data?.message || 'Excel report downloaded successfully');
                } else {
                    toast.error('File URL not received');
                }
            } else {
                toast.error(res?.data?.message || 'Failed to download Excel report');
            }
        } catch (err) {
            console.error('Error exporting reorder excel:', err);
            toast.error(err?.response?.data?.message || 'Download failed');
        } finally {
            setIsExportingReorder(false);
        }
    };

    // Filter states for Item-wise Stock Table
    const [stockSearch, setStockSearch] = useState('');
    const [stockStatusFilter, setStockStatusFilter] = useState('ALL'); // 'ALL' | 'IN_STOCK' | 'OUT_OF_STOCK'
    const [stockPage, setStockPage] = useState(1);
    const stockPerPage = 10;
    const [isExportingStock, setIsExportingStock] = useState(false);

    const handleExportStockExcel = async () => {
        try {
            setIsExportingStock(true);
            const payload = {
                search: stockSearch || '',
                firm_id: localStorage.getItem('PAY_USER_FIRM_ID'),
                year_id: localStorage.getItem('PAY_USER_YEAR_ID'),
            };

            const res = await axios.post(
                `${V_URL}/prosafe/pro-ms-stock-value-xlsx-download`,
                payload,
                {
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('PAY_USER_TOKEN'),
                    },
                }
            );

            if (res?.data && res?.data?.success) {
                const url = res?.data?.data?.url;
                if (url) {
                    window.open(url, '_blank');
                    toast.success(res?.data?.message || 'Excel downloaded successfully');
                } else {
                    toast.error('File URL not received');
                }
            } else {
                toast.error(res?.data?.message || 'Failed to download Excel report');
            }
        } catch (err) {
            console.error('Error exporting stock excel:', err);
            toast.error(err?.response?.data?.message || 'Download failed');
        } finally {
            setIsExportingStock(false);
        }
    };

    // Filtered Transactions
    const filteredTransactions = useMemo(() => {
        if (!dashboardData?.transactions) return [];
        return dashboardData.transactions.filter(t => {
            // Tag Filter
            if (transTagFilter === 'PO' && t.tag_number !== 10) return false;
            if (transTagFilter === 'PR' && t.tag_number !== 9) return false;
            if (transTagFilter === 'PU' && t.tag_number !== 11) return false;
            if (transTagFilter === 'ISSUE' && t.tag_number !== 13) return false;

            // Search Filter
            if (transSearch && transSearch.trim() !== '') {
                const searchLower = transSearch.trim().toLowerCase();
                const vNo = String(t.voucher_no || '').toLowerCase();
                const party = String(t.party_name || '').toLowerCase();
                const proj = String(t.project_name || '').toLowerCase();
                const billNo = String(t.bill_no || '').toLowerCase();
                return vNo.includes(searchLower) || party.includes(searchLower) || proj.includes(searchLower) || billNo.includes(searchLower);
            }
            return true;
        });
    }, [dashboardData?.transactions, transTagFilter, transSearch]);

    // Paginated Transactions
    const paginatedTransactions = useMemo(() => {
        const start = (transPage - 1) * transPerPage;
        return filteredTransactions.slice(start, start + transPerPage);
    }, [filteredTransactions, transPage]);

    // Filtered Parties
    const filteredParties = useMemo(() => {
        if (!dashboardData?.party_details) return [];
        return dashboardData.party_details.filter(p => {
            if (partySearch && partySearch.trim() !== '') {
                const searchLower = partySearch.trim().toLowerCase();
                const name = String(p.party_name || '').toLowerCase();
                const code = String(p.party_code || '').toLowerCase();
                return name.includes(searchLower) || code.includes(searchLower);
            }
            return true;
        });
    }, [dashboardData?.party_details, partySearch]);

    // Paginated Parties
    const paginatedParties = useMemo(() => {
        const start = (partyPage - 1) * partyPerPage;
        return filteredParties.slice(start, start + partyPerPage);
    }, [filteredParties, partyPage]);

    // Filtered Re-Order Items
    const filteredReorders = useMemo(() => {
        if (!dashboardData?.reorder_items) return [];
        return dashboardData.reorder_items.filter(item => {
            const balance = Number(item.balance || 0);
            if (reorderStockFilter === 'OUT_OF_STOCK' && balance > 0) return false;
            if (reorderStockFilter === 'LOW_STOCK' && balance <= 0) return false;

            if (reorderSearch && reorderSearch.trim() !== '') {
                const s = reorderSearch.trim().toLowerCase();
                const name = String(item.item_name || '').toLowerCase();
                const code = String(item.m_code || '').toLowerCase();
                const itemId = String(item.ItemId || '').toLowerCase();
                const hsn = String(item.hsn_code || '').toLowerCase();
                const cat = String(item.category || '').toLowerCase();
                const unit = String(item.unit || '').toLowerCase();
                const party = String(item.lastPurchaseParty || '').toLowerCase();
                const issueParty = String(item.lastIssueParty || '').toLowerCase();
                return name.includes(s) || code.includes(s) || itemId.includes(s) || hsn.includes(s) || cat.includes(s) || unit.includes(s) || party.includes(s) || issueParty.includes(s);
            }
            return true;
        });
    }, [dashboardData?.reorder_items, reorderStockFilter, reorderSearch]);

    // Paginated Re-Order Items
    const paginatedReorders = useMemo(() => {
        const start = (reorderPage - 1) * reorderPerPage;
        return filteredReorders.slice(start, start + reorderPerPage);
    }, [filteredReorders, reorderPage]);

    // Filtered Item-wise Stocks (Excluding items where total in, out, and current stock are all zero)
    const activeItemStocks = useMemo(() => {
        if (!dashboardData?.item_stocks) return [];
        return dashboardData.item_stocks.filter(item => {
            const totalIn = Number(item.total_in || 0);
            const totalOut = Number(item.total_out || 0);
            const balance = Number(item.balance || 0);
            return totalIn !== 0 || totalOut !== 0 || balance !== 0;
        });
    }, [dashboardData?.item_stocks]);

    const filteredStocks = useMemo(() => {
        return activeItemStocks.filter(item => {
            const balance = Number(item.balance || 0);
            if (stockStatusFilter === 'IN_STOCK' && balance <= 0) return false;
            if (stockStatusFilter === 'OUT_OF_STOCK' && balance > 0) return false;

            if (stockSearch && stockSearch.trim() !== '') {
                const s = stockSearch.trim().toLowerCase();
                const name = String(item.item_name || '').toLowerCase();
                const code = String(item.m_code || '').toLowerCase();
                const itemId = String(item.ItemId || '').toLowerCase();
                const hsn = String(item.hsn_code || '').toLowerCase();
                const cat = String(item.category || '').toLowerCase();
                const unit = String(item.unit || '').toLowerCase();
                return name.includes(s) || code.includes(s) || itemId.includes(s) || hsn.includes(s) || cat.includes(s) || unit.includes(s);
            }
            return true;
        });
    }, [activeItemStocks, stockStatusFilter, stockSearch]);

    // Paginated Item-wise Stocks
    const paginatedStocks = useMemo(() => {
        const start = (stockPage - 1) * stockPerPage;
        return filteredStocks.slice(start, start + stockPerPage);
    }, [filteredStocks, stockPage]);

    // Listing Status Badges
    const renderStatusBadge = (status) => {
        switch (status) {
            case 1:
                return <span className="custom-badge status-orange">Pending</span>;
            case 2:
                return <span className="custom-badge status-blue">Verified</span>;
            case 3:
                return <span className="custom-badge status-green">Approved</span>;
            case 4:
                return <span className="custom-badge status-red">Rejected</span>;
            default:
                return <span className="custom-badge status-gray">N/A</span>;
        }
    };

    // Listing Tag Badges
    const renderTagBadge = (tagNumber, tagName) => {
        switch (tagNumber) {
            case 10:
                return <span className="custom-badge status-blue">PO</span>;
            case 9:
                return <span className="custom-badge status-purple" style={{ backgroundColor: '#e0f2fe', color: '#0284c7' }}>PR</span>;
            case 11:
                return <span className="custom-badge status-green">PURCHASE RECEIVING</span>;
            case 13:
                return <span className="custom-badge status-orange">ISSUE</span>;
            case 12:
                return <span className="custom-badge status-yellow">PO RETURN</span>;
            case 14:
                return <span className="custom-badge status-red">ISSUE RETURN</span>;
            default:
                return <span className="custom-badge status-gray">{tagName || 'TRANS'}</span>;
        }
    };

    const reorderCount = dashboardData?.total_reorder_items ?? dashboardData?.reorder_items?.length ?? 0;

    return (
        <div className={`main-wrapper ${isSidebarOpen ? 'slide-nav' : ''}`}>

            <Header handleOpen={handleOpen} />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-12 col-sm-8 mb-2 mb-sm-0">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to='/main-store/user/dashboard'>Dashboard </Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right"></i>
                                    </li>
                                    <li className="breadcrumb-item active">Main Store Dashboard</li>
                                </ul>
                            </div>
                            <div className="col-12 col-sm-4 text-sm-end">
                                <button className="btn btn-sm btn-outline-primary" onClick={() => dispatch(getStoreDashboard())} disabled={isLoading}>
                                    <i className={`feather-refresh-cw me-1 ${isLoading ? 'spin' : ''}`}></i> Refresh Data
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Older Theme Greeting Card UI */}
                    <div className="good-morning-blk">
                        <div className="row">
                            <div className="col-md-6 col-sm-6">
                                <div className="morning-user">
                                    <h2>
                                        Good Morning, <span>{localStorage.getItem('PAY_USER_NAME') || 'Believe Engitech'}</span>
                                    </h2>
                                    <p>Have a nice day at work</p>
                                </div>
                            </div>
                            <div className="col-md-6 col-sm-6 position-blk">
                                <div className="morning-img">
                                    <img src="/assets/img/morning-img-01.svg" alt="" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Admin Dashboard Metric Cards with Lucide Icons */}
                    <div className="row">
                        {/* PO Card */}
                        <div className="col-md-6 col-sm-6 col-lg-6 col-xl-4">
                            <div className="dash-widget">
                                <div className="dash-boxs comman-flex-center">
                                    <ShoppingBag size={24} style={{ color: 'var(--primary-clr, #ff6700)' }} />
                                </div>
                                <div className="dash-content dash-count">
                                    <h4>PO Count</h4>
                                    <h2>
                                        <CountUp end={dashboardData?.total_po || 0} duration={1.2} />
                                    </h2>
                                    <p>
                                        <span className="passive-view">
                                            <i className="feather-arrow-up-right me-1"></i>
                                        </span>
                                        Purchase Orders
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* PR Card */}
                        <div className="col-md-6 col-sm-6 col-lg-6 col-xl-4">
                            <div className="dash-widget">
                                <div className="dash-boxs comman-flex-center">
                                    <FileText size={24} style={{ color: 'var(--primary-clr, #ff6700)' }} />
                                </div>
                                <div className="dash-content dash-count">
                                    <h4>PR Count</h4>
                                    <h2>
                                        <CountUp end={dashboardData?.total_pr || 0} duration={1.2} />
                                    </h2>
                                    <p>
                                        <span className="passive-view">
                                            <i className="feather-arrow-up-right me-1"></i>
                                        </span>
                                        Requisitions
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* ISSUE Card */}
                        <div className="col-md-6 col-sm-6 col-lg-6 col-xl-4">
                            <div className="dash-widget">
                                <div className="dash-boxs comman-flex-center">
                                    <Send size={24} style={{ color: 'var(--primary-clr, #ff6700)' }} />
                                </div>
                                <div className="dash-content dash-count">
                                    <h4>ISSUE Count</h4>
                                    <h2>
                                        <CountUp end={dashboardData?.total_issue || 0} duration={1.2} />
                                    </h2>
                                    <p>
                                        <span className="passive-view">
                                            <i className="feather-arrow-up-right me-1"></i>
                                        </span>
                                        Material Issues
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* PURCHASE RECEIVING Card */}
                        <div className="col-md-6 col-sm-6 col-lg-6 col-xl-4">
                            <div className="dash-widget">
                                <div className="dash-boxs comman-flex-center">
                                    <Package size={24} style={{ color: 'var(--primary-clr, #ff6700)' }} />
                                </div>
                                <div className="dash-content dash-count">
                                    <h4>RECEIVING</h4>
                                    <h2>
                                        <CountUp end={dashboardData?.total_purchase_receiving || 0} duration={1.2} />
                                    </h2>
                                    <p>
                                        <span className="passive-view">
                                            <i className="feather-arrow-up-right me-1"></i>
                                        </span>
                                        Material Receipts
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* ITEM Card */}
                        <div className="col-md-6 col-sm-6 col-lg-6 col-xl-4">
                            <div className="dash-widget">
                                <div className="dash-boxs comman-flex-center">
                                    <Box size={24} style={{ color: 'var(--primary-clr, #ff6700)' }} />
                                </div>
                                <div className="dash-content dash-count">
                                    <h4>ITEM Count</h4>
                                    <h2>
                                        <CountUp end={dashboardData?.total_items || 0} duration={1.2} />
                                    </h2>
                                    <p>
                                        <span className="passive-view">
                                            <i className="feather-arrow-up-right me-1"></i>
                                        </span>
                                        Catalog Items
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* PARTY Card */}
                        <div className="col-md-6 col-sm-6 col-lg-6 col-xl-4">
                            <div className="dash-widget">
                                <div className="dash-boxs comman-flex-center">
                                    <Users size={24} style={{ color: 'var(--primary-clr, #ff6700)' }} />
                                </div>
                                <div className="dash-content dash-count">
                                    <h4>PARTY Count</h4>
                                    <h2>
                                        <CountUp end={dashboardData?.total_parties || 0} duration={1.2} />
                                    </h2>
                                    <p>
                                        <span className="passive-view">
                                            <i className="feather-arrow-up-right me-1"></i>
                                        </span>
                                        Active Vendors
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Item Wise Purchase Chart Section */}
                    {/* <ItemWisePurchaseChart /> */}

                    {/* Section 1: Re-Order Quantity Management and How Much Stock Table */}
                    <div className="row mb-4">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    {/* Listing Header Controls */}
                                    <div className="page-table-header mb-3">
                                        <div className="row align-items-center">
                                            <div className="col-12 col-md-6 mb-2 mb-md-0">
                                                <div className="doctor-table-blk">
                                                    <h3>
                                                        Re-Order Quantity & Stock Management
                                                        {reorderCount > 0 && (
                                                            <span className="badge bg-danger ms-2" style={{ fontSize: '11px', padding: '4px 8px', borderRadius: '12px' }}>
                                                                {reorderCount} Low Stock
                                                            </span>
                                                        )}
                                                    </h3>
                                                    {/* Native Search Box */}
                                                    <div className="doctor-search-blk">
                                                        <div className="top-nav-search table-search-blk">
                                                            <form onSubmit={(e) => e.preventDefault()}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search item, category..."
                                                                    value={reorderSearch}
                                                                    onChange={(e) => { setReorderSearch(e.target.value); setReorderPage(1); }}
                                                                />
                                                                <button className="btn" type="button">
                                                                    <img src="/assets/img/icons/search-normal.svg" alt="Search" />
                                                                </button>
                                                            </form>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 text-md-end d-flex flex-wrap align-items-center justify-content-md-end gap-2">
                                                {/* Filter Tabs */}
                                                <div className="custom-filter-tabs">
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${reorderStockFilter === 'ALL' ? 'active-tab' : ''}`}
                                                        onClick={() => { setReorderStockFilter('ALL'); setReorderPage(1); }}
                                                    >
                                                        All ({dashboardData?.reorder_items?.length || 0})
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${reorderStockFilter === 'OUT_OF_STOCK' ? 'active-tab' : ''}`}
                                                        onClick={() => { setReorderStockFilter('OUT_OF_STOCK'); setReorderPage(1); }}
                                                    >
                                                        Out of Stock ({dashboardData?.reorder_items?.filter(i => Number(i.balance || 0) <= 0)?.length || 0})
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${reorderStockFilter === 'LOW_STOCK' ? 'active-tab' : ''}`}
                                                        onClick={() => { setReorderStockFilter('LOW_STOCK'); setReorderPage(1); }}
                                                    >
                                                        Low Stock ({dashboardData?.reorder_items?.filter(i => Number(i.balance || 0) > 0)?.length || 0})
                                                    </button>
                                                </div>
                                                <Link to="/main-store/user/reorder-items" className="btn btn-sm btn-outline-primary" style={{ whiteSpace: 'nowrap' }}>
                                                    <i className="feather-file-text me-1"></i> Full Report
                                                </Link>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success border border-success"
                                                    style={{ whiteSpace: 'nowrap' }}
                                                    onClick={handleExportReorderExcel}
                                                    disabled={isExportingReorder}
                                                >
                                                    <i className={`feather-file me-1 ${isExportingReorder ? 'spin' : ''}`}></i>
                                                    {isExportingReorder ? 'Exporting...' : 'Export Excel'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Listing Table */}
                                    <div className="table-responsive">
                                        <table className="table border-0 custom-table comman-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Sr.</th>
                                                    <th>Item Details</th>
                                                    <th>Category & Unit</th>
                                                    <th className="text-center">Current Stock</th>
                                                    <th className="text-center">Re-Order Level</th>
                                                    <th className="text-center">Required Order Qty</th>
                                                    <th>Stock Status</th>
                                                    <th>Last Purchase</th>
                                                    <th>Last Issue</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-4 text-muted">
                                                            <div className="spinner-border spinner-border-sm text-danger me-2" role="status"></div>
                                                            Loading re-order quantity & stock data...
                                                        </td>
                                                    </tr>
                                                ) : paginatedReorders.length > 0 ? (
                                                    paginatedReorders.map((item, idx) => {
                                                        const balance = Number(item.balance || 0);
                                                        const reorderQty = Number(item.reorder_quantity || 0);
                                                        const orderQty = Number(item.order_qty || (reorderQty - balance));
                                                        const isOutOfStock = balance <= 0;

                                                        return (
                                                            <tr key={item.item_id || idx}>
                                                                <td>{(reorderPage - 1) * reorderPerPage + idx + 1}</td>
                                                                <td>
                                                                    <span className="fw-bold d-block text-dark">{item.item_name || 'N/A'}</span>
                                                                    <div className="fs-12 text-muted">
                                                                        {item.hsn_code && <span>HSN: {item.hsn_code}</span>}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="d-block">{item.category || '-'}</span>
                                                                    <span className="text-muted fs-11 mt-1">{item.unit || ''}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className={`fw-bold fs-14 ${isOutOfStock ? 'text-danger' : 'text-warning'}`}>
                                                                        {balance.toFixed(2)}
                                                                    </span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="fw-semibold text-secondary">
                                                                        {reorderQty.toFixed(2)}
                                                                    </span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="text-danger" style={{ fontWeight: 'bold' }}>
                                                                        {orderQty > 0 ? orderQty.toFixed(2) : '0.00'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {isOutOfStock ? (
                                                                        <span className="text-danger">Out of Stock</span>
                                                                    ) : (
                                                                        <span className="text-warning">Low Stock</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {item.lastPurchaseDate ? (
                                                                        <div className="fs-12">
                                                                            <div className="fw-semibold">{moment(item.lastPurchaseDate).format('DD-MM-YYYY')}</div>
                                                                            <div className="text-muted text-truncate" style={{ maxWidth: '140px' }} title={item.lastPurchaseParty}>
                                                                                {item.lastPurchaseParty || '-'}
                                                                            </div>
                                                                            {item.lastPurchaseRate && <div className="text-success">₹ {Number(item.lastPurchaseRate).toFixed(2)}</div>}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-muted fs-12">No Purchase</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {item.lastIssueDate ? (
                                                                        <div className="fs-12">
                                                                            <div className="fw-semibold">{moment(item.lastIssueDate).format('DD-MM-YYYY')}</div>
                                                                            {item.lastIssueParty && <div className="text-muted text-truncate" style={{ maxWidth: '140px' }}>{item.lastIssueParty}</div>}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-muted fs-12">-</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="9">
                                                            <div className="no-table-data text-center py-4">
                                                                <div className="text-success mb-1 fw-bold">
                                                                    <i className="feather-check-circle fs-16 me-1"></i> All Items Above Re-Order Level
                                                                </div>
                                                                <span className="text-muted fs-12">No items currently require replenishment based on configured thresholds.</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Footer */}
                                    <div className="row align-center mt-3 mb-2">
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_info" role="status" aria-live="polite">
                                                Showing {Math.min(reorderPerPage, filteredReorders.length)} from {filteredReorders.length} items
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_paginate paging_simple_numbers">
                                                <Pagination
                                                    total={filteredReorders.length}
                                                    itemsPerPage={reorderPerPage}
                                                    currentPage={reorderPage}
                                                    onPageChange={(page) => setReorderPage(page)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Item-wise Stock Management Table */}
                    <div className="row mb-4">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    {/* Listing Header Controls */}
                                    <div className="page-table-header mb-3">
                                        <div className="row align-items-center">
                                            <div className="col-12 col-md-6 mb-2 mb-md-0">
                                                <div className="doctor-table-blk">
                                                    <h3>Item-wise Stock Management</h3>
                                                    {/* Native Search Box */}
                                                    <div className="doctor-search-blk">
                                                        <div className="top-nav-search table-search-blk">
                                                            <form onSubmit={(e) => e.preventDefault()}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search item, category..."
                                                                    value={stockSearch}
                                                                    onChange={(e) => { setStockSearch(e.target.value); setStockPage(1); }}
                                                                />
                                                                <button className="btn" type="button">
                                                                    <img src="/assets/img/icons/search-normal.svg" alt="Search" />
                                                                </button>
                                                            </form>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 text-md-end d-flex flex-wrap align-items-center justify-content-md-end gap-2">
                                                {/* Filter Tabs */}
                                                <div className="custom-filter-tabs">
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${stockStatusFilter === 'ALL' ? 'active-tab' : ''}`}
                                                        onClick={() => { setStockStatusFilter('ALL'); setStockPage(1); }}
                                                    >
                                                        All ({activeItemStocks.length})
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${stockStatusFilter === 'IN_STOCK' ? 'active-tab' : ''}`}
                                                        onClick={() => { setStockStatusFilter('IN_STOCK'); setStockPage(1); }}
                                                    >
                                                        In Stock ({activeItemStocks.filter(i => Number(i.balance || 0) > 0).length})
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${stockStatusFilter === 'OUT_OF_STOCK' ? 'active-tab' : ''}`}
                                                        onClick={() => { setStockStatusFilter('OUT_OF_STOCK'); setStockPage(1); }}
                                                    >
                                                        Out of Stock / Negative ({activeItemStocks.filter(i => Number(i.balance || 0) <= 0).length})
                                                    </button>
                                                </div>
                                                <Link to="/main-store/user/stock-with-value-management" className="btn btn-sm btn-outline-primary" style={{ whiteSpace: 'nowrap' }}>
                                                    <i className="feather-layers me-1"></i> Full Stock Report
                                                </Link>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success border border-success"
                                                    style={{ whiteSpace: 'nowrap' }}
                                                    onClick={handleExportStockExcel}
                                                    disabled={isExportingStock}
                                                >
                                                    <i className={`feather-file me-1 ${isExportingStock ? 'spin' : ''}`}></i>
                                                    {isExportingStock ? 'Exporting...' : 'Export Excel'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Listing Table */}
                                    <div className="table-responsive">
                                        <table className="table border-0 custom-table comman-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Sr.</th>
                                                    <th>Item Details</th>
                                                    <th>Category</th>
                                                    <th>Unit</th>
                                                    <th className="text-center">Total In</th>
                                                    <th className="text-center">Total Out</th>
                                                    <th className="text-center">Current Stock</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">
                                                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                                            Loading item stock data...
                                                        </td>
                                                    </tr>
                                                ) : paginatedStocks.length > 0 ? (
                                                    paginatedStocks.map((item, idx) => {
                                                        const balance = Number(item.balance || 0);
                                                        const totalIn = Number(item.total_in || 0);
                                                        const totalOut = Number(item.total_out || 0);
                                                        const isPositive = balance > 0;
                                                        const isNegative = balance < 0;

                                                        return (
                                                            <tr key={item.item_id || idx}>
                                                                <td>{(stockPage - 1) * stockPerPage + idx + 1}</td>
                                                                <td>
                                                                    <span className="fw-bold d-block text-dark">{item.item_name || 'N/A'}</span>
                                                                    <div className="fs-12 text-muted d-flex gap-2">
                                                                        {item.hsn_code && <span>HSN: {item.hsn_code}</span>}
                                                                    </div>
                                                                </td>
                                                                <td>{item.category || '-'}</td>
                                                                <td>
                                                                    {item.unit || ''}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="text-success fw-semibold">{totalIn.toFixed(2)}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="text-muted fw-semibold">{totalOut.toFixed(2)}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className={`fw-bold fs-14 ${isNegative ? 'text-danger' : isPositive ? 'text-success' : 'text-muted'}`}>
                                                                        {balance.toFixed(2)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {isNegative ? (
                                                                        <span className="text-danger">Negative Stock</span>
                                                                    ) : balance === 0 ? (
                                                                        <span className="text-muted">Zero Stock</span>
                                                                    ) : (
                                                                        <span className="text-success">In Stock</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8">
                                                            <div className="no-table-data text-center py-4">
                                                                No Item Stock Data Found!
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Footer */}
                                    <div className="row align-center mt-3 mb-2">
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_info" role="status" aria-live="polite">
                                                Showing {Math.min(stockPerPage, filteredStocks.length)} from {filteredStocks.length} items
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_paginate paging_simple_numbers">
                                                <Pagination
                                                    total={filteredStocks.length}
                                                    itemsPerPage={stockPerPage}
                                                    currentPage={stockPage}
                                                    onPageChange={(page) => setStockPage(page)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Transaction Data Register Table as per Listing Design */}
                    <div className="row mb-4">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    {/* Listing Header Controls */}
                                    <div className="page-table-header mb-3">
                                        <div className="row align-items-center">
                                            <div className="col-12 col-md-6 mb-2 mb-md-0">
                                                <div className="doctor-table-blk">
                                                    <h3>Transaction Data Register</h3>
                                                    {/* Native Listing Search Box as per theme & screenshot */}
                                                    <div className="doctor-search-blk">
                                                        <div className="top-nav-search table-search-blk">
                                                            <form onSubmit={(e) => e.preventDefault()}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search here"
                                                                    value={transSearch}
                                                                    onChange={(e) => { setTransSearch(e.target.value); setTransPage(1); }}
                                                                />
                                                                <button className="btn" type="button">
                                                                    <img src="/assets/img/icons/search-normal.svg" alt="Search" />
                                                                </button>
                                                            </form>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 text-md-end">
                                                {/* Custom Filter Tabs matching screenshot */}
                                                <div className="custom-filter-tabs justify-content-md-end">
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${transTagFilter === 'ALL' ? 'active-tab' : ''}`}
                                                        onClick={() => { setTransTagFilter('ALL'); setTransPage(1); }}
                                                    >
                                                        All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${transTagFilter === 'PO' ? 'active-tab' : ''}`}
                                                        onClick={() => { setTransTagFilter('PO'); setTransPage(1); }}
                                                    >
                                                        PO
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${transTagFilter === 'PR' ? 'active-tab' : ''}`}
                                                        onClick={() => { setTransTagFilter('PR'); setTransPage(1); }}
                                                    >
                                                        PR
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${transTagFilter === 'PU' ? 'active-tab' : ''}`}
                                                        onClick={() => { setTransTagFilter('PU'); setTransPage(1); }}
                                                    >
                                                        Receiving
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`filter-tab-item ${transTagFilter === 'ISSUE' ? 'active-tab' : ''}`}
                                                        onClick={() => { setTransTagFilter('ISSUE'); setTransPage(1); }}
                                                    >
                                                        Issue
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Listing Table */}
                                    <div className="table-responsive">
                                        <table className="table border-0 custom-table comman-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Sr.</th>
                                                    <th>Voucher No</th>
                                                    <th>Date</th>
                                                    <th>Type / Tag</th>
                                                    <th>Party Name</th>
                                                    <th>Project</th>
                                                    <th className="text-center">Items</th>
                                                    <th className="text-end">Total Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-4 text-muted">
                                                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                                                            Loading transaction data...
                                                        </td>
                                                    </tr>
                                                ) : paginatedTransactions.length > 0 ? (
                                                    paginatedTransactions.map((t, idx) => (
                                                        <tr key={t._id || idx}>
                                                            <td>{(transPage - 1) * transPerPage + idx + 1}</td>
                                                            <td>
                                                                <span className="fw-bold">{t.voucher_no || 'N/A'}</span>
                                                                {t.bill_no && <div className="fs-12 text-muted">Bill: {t.bill_no}</div>}
                                                            </td>
                                                            <td>
                                                                {t.trans_date ? moment(t.trans_date).format('DD-MM-YYYY') : '-'}
                                                            </td>
                                                            <td>
                                                                {renderTagBadge(t.tag_number, t.tag_name)}
                                                            </td>
                                                            <td>{t.party_name || '-'}</td>
                                                            <td>{t.project_name || '-'}</td>
                                                            <td className="text-center">{t.item_count || 0}</td>
                                                            <td className="text-end">
                                                                ₹ {Number(t.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="9">
                                                            <div className="no-table-data text-center py-3">
                                                                No Data Found!
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Footer as per Listing Design */}
                                    <div className="row align-center mt-3 mb-2">
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_info" id="DataTables_Table_0_info" role="status" aria-live="polite">
                                                Showing {Math.min(transPerPage, filteredTransactions.length)} from {filteredTransactions.length} data
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_paginate paging_simple_numbers" id="DataTables_Table_0_paginate">
                                                <Pagination
                                                    total={filteredTransactions.length}
                                                    itemsPerPage={transPerPage}
                                                    currentPage={transPage}
                                                    onPageChange={(page) => setTransPage(page)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 4: Party-wise PO Breakdown Table as per Listing Design */}
                    <div className="row mb-4">
                        <div className="col-sm-12">
                            <div className="card card-table show-entire">
                                <div className="card-body">
                                    {/* Listing Header Controls */}
                                    <div className="page-table-header mb-3">
                                        <div className="row align-items-center">
                                            <div className="col-12 col-md-6 mb-2 mb-md-0">
                                                <div className="doctor-table-blk">
                                                    <h3>Party-wise PO & Purchase Receiving Breakdown</h3>
                                                    {/* Native Listing Search Box as per theme & screenshot */}
                                                    <div className="doctor-search-blk">
                                                        <div className="top-nav-search table-search-blk">
                                                            <form onSubmit={(e) => e.preventDefault()}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search here"
                                                                    value={partySearch}
                                                                    onChange={(e) => { setPartySearch(e.target.value); setPartyPage(1); }}
                                                                />
                                                                <button className="btn" type="button">
                                                                    <img src="/assets/img/icons/search-normal.svg" alt="Search" />
                                                                </button>
                                                            </form>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Listing Table */}
                                    <div className="table-responsive">
                                        <table className="table border-0 custom-table comman-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Sr.</th>
                                                    <th>Party Name</th>
                                                    <th>Party Code</th>
                                                    <th className="text-center">Purchase Orders (PO)</th>
                                                    <th className="text-center">Purchase Receivings</th>
                                                    <th className="text-center">PR Count</th>
                                                    <th>Last Activity Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">
                                                            <div className="spinner-border spinner-border-sm text-success me-2" role="status"></div>
                                                            Loading party summary...
                                                        </td>
                                                    </tr>
                                                ) : paginatedParties.length > 0 ? (
                                                    paginatedParties.map((p, idx) => (
                                                        <tr key={p._id || idx}>
                                                            <td>{(partyPage - 1) * partyPerPage + idx + 1}</td>
                                                            <td>
                                                                {p.party_name}
                                                            </td>
                                                            <td>
                                                                {p.party_code || '-'}
                                                            </td>
                                                            <td className="text-center">
                                                                {p.po_count}
                                                            </td>
                                                            <td className="text-center">
                                                                {p.receiving_count}
                                                            </td>
                                                            <td className="text-center">{p.pr_count || 0}</td>
                                                            <td>
                                                                {p.last_trans_date ? moment(p.last_trans_date).format('DD-MM-YYYY') : '-'}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8">
                                                            <div className="no-table-data text-center py-3">
                                                                No Data Found!
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Footer as per Listing Design */}
                                    <div className="row align-center mt-3 mb-2">
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_info" id="DataTables_Table_0_info" role="status" aria-live="polite">
                                                Showing {Math.min(partyPerPage, filteredParties.length)} from {filteredParties.length} data
                                            </div>
                                        </div>
                                        <div className="col-sm-12 col-md-6 col-lg-6 col-xxl-6">
                                            <div className="dataTables_paginate paging_simple_numbers" id="DataTables_Table_0_paginate">
                                                <Pagination
                                                    total={filteredParties.length}
                                                    itemsPerPage={partyPerPage}
                                                    currentPage={partyPage}
                                                    onPageChange={(page) => setPartyPage(page)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 5: Project-wise Material Usage Summary */}
                    <ProjectUsageSummary />

                </div>
                <Footer />
            </div>
        </div>
    );
};

export default Dashboard;